import React, { useState, useCallback, useRef } from 'react';
import { stagePublicAPI } from '../services/api';
import ChampDate from './ChampDate';
import './StagePublic.css';

/**
 * SUIVI DE STAGE — le PORTAIL DE DÉPÔT (lot 3.4 bis)
 * ==================================================
 * Spec : doc/modules/suivi_de_stage.md, étape ⑦, écarts E81, E82, E94
 * Maquette : l'aperçu « Portail dépôt documents (vue étudiant) » — trois zones de
 * dépôt, « PDF · Max 10 Mo », un bouton final.
 *
 * ── CE QUE LA PAGE MONTRE AVANT L'IDENTIFICATION ───────────────────────────
 * Le nom, la formation et la période — pour qu'un destinataire par erreur voie
 * que ce n'est pas lui. JAMAIS le numéro ni la date de naissance : ce sont eux
 * qu'on lui demande (E79, E82).
 *
 * ── UNE PIÈCE, UN ENVOI (E94) ──────────────────────────────────────────────
 * Chaque fichier part DÈS qu'il est choisi (ou glissé) : le proxy du NAS laisse
 * passer 25 Mo par requête, trois pièces ensemble n'y tiendraient pas. Le bouton
 * « Envoyer mes documents » clôt le dépôt : avant, tout se remplace.
 *
 * ⚠️ L'IDENTITÉ EST REDEMANDÉE À CHAQUE GESTE, côté serveur : la page la garde en
 * mémoire le temps de la visite, et ne l'écrit nulle part (ni stockage, ni adresse).
 */

// Lot C.2 : la carte de pièce, le message et les tailles servent AUSSI la page de dépôt de la
// Certification (StageCertifDepotPublic) — exportés plutôt que recopiés.
export const tailleMo = (o) => (o / 1024 / 1024).toFixed(1).replace('.', ',') + ' Mo';
// ⚠️ Vu à l'image : un fichier de 1 Ko s'affichait « 0,0 Mo ».
export const tailleLisible = (o) => (o < 1024 * 1024 ? Math.max(1, Math.round(o / 1024)) + ' Ko' : tailleMo(o));
export const dateFr = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '');
const LIBELLE_FORMAT = { pdf: 'PDF', jpeg: 'JPEG', png: 'PNG' };
const ACCEPT = { pdf: 'application/pdf,.pdf', jpeg: 'image/jpeg,.jpg,.jpeg', png: 'image/png,.png' };

export function Message({ ton, icone, titre, children }) {
    return (
        <div className="pub-page">
            <div className={'pub-message' + (ton ? ' pub-message-' + ton : '')}>
                <i className={'fa-solid ' + icone} />
                <h2>{titre}</h2>
                {children}
            </div>
        </div>
    );
}

/** Une pièce attendue : son état, sa zone de dépôt, ses messages. */
export function Piece({ piece, ouvert, tailleMax, enCours, onFichier, libelleAcquise = 'Validée' }) {
    const [survol, setSurvol] = useState(false);
    const ref = useRef(null);
    const c = piece.courante;
    const acquise = c && c.statut === 'valide';
    const enVerif = c && c.soumise && c.statut === 'depose';
    const rejetee = c && c.statut === 'rejete';
    const peutDeposer = ouvert && !acquise && !enVerif && !enCours;
    const formats = (piece.formats || []).map((f) => LIBELLE_FORMAT[f] || f);

    const gestes = {
        onDragOver: (e) => { e.preventDefault(); if (peutDeposer && !survol) setSurvol(true); },
        onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSurvol(false); },
        onDrop: (e) => {
            e.preventDefault(); setSurvol(false);
            if (!peutDeposer) return;
            const f = e.dataTransfer.files;
            if (f && f.length === 1) onFichier(piece, f[0]);
            else if (f && f.length > 1) onFichier(piece, null, 'Déposez UN seul fichier pour cette pièce.');
        }
    };

    return (
        <div className={'pub-piece' + (acquise ? ' acquise' : '') + ((rejetee || c?.alerte) ? ' rejetee' : '')}>
            <div className="pub-piece-tete">
                <strong>{piece.libelle}</strong>
                {piece.obligatoire ? <span className="form-required"> *</span> : <span className="pub-discret"> (facultatif)</span>}
                {acquise && <span className="pub-badge ok"><i className="fa-solid fa-check" /> {libelleAcquise}</span>}
                {enVerif && <span className="pub-badge attente"><i className="fa-solid fa-hourglass-half" /> En cours de vérification</span>}
            </div>

            {rejetee && (
                <div className="pub-piece-motif">
                    <i className="fa-solid fa-circle-exclamation" /> <b>À corriger :</b> {c.motif_rejet}
                </div>
            )}

            {c && !rejetee && (
                <div className="pub-piece-fichier">
                    <i className="fa-solid fa-file-lines" /> {c.nom_fichier}
                    <span className="pub-discret"> · {tailleLisible(c.taille_octets)}{!c.soumise && ' · déposée, pas encore envoyée'}</span>
                </div>
            )}

            {/* ⚠️ 2026-09-26 : le fichier à REMPLACER, et pourquoi — même fichier pour deux
                pièces, même fichier que la convention, fichier déjà rejeté, fichier d'un autre dossier.
                Tant qu'il est là, l'envoi est bloqué (et le serveur le refuse aussi). */}
            {c?.alerte && (
                <div className="pub-piece-motif">
                    <i className="fa-solid fa-triangle-exclamation" /> <b>À remplacer :</b> {c.alerte}
                </div>
            )}

            {peutDeposer && (
                <button type="button" className={'pub-zone' + (survol ? ' survol' : '')}
                        onClick={() => ref.current && ref.current.click()} {...gestes}>
                    <i className={'fa-solid ' + (survol ? 'fa-file-arrow-down' : 'fa-cloud-arrow-up')} />
                    <span>{survol ? 'Relâchez pour déposer'
                        : (c && !rejetee ? 'Remplacer : cliquez ou glissez un autre fichier' : 'Cliquez ou glissez votre fichier ici')}</span>
                    <span className="pub-discret">
                        {formats.length > 1 ? 'Formats acceptés' : 'Format accepté'} : {formats.join(', ')} · Max {tailleMo(tailleMax)}
                    </span>
                </button>
            )}
            <input ref={ref} type="file" style={{ display: 'none' }}
                   accept={(piece.formats || []).map((f) => ACCEPT[f]).filter(Boolean).join(',')}
                   onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onFichier(piece, f); }} />
        </div>
    );
}

/**
 * ⚠️ LOT 3.8b — L'APERÇU DE L'ÉDITEUR DE MODÈLES, C'EST CETTE PAGE (E116).
 * `apercu` = un état de portail FICTIF : pas d'identification, et aucun appel au
 * serveur — un dépôt s'affiche « déposé » localement, l'envoi ne part pas.
 */
const raisonApercu = (pieces) => {
    const manquantes = pieces.filter((x) => x.obligatoire && !x.courante);
    if (manquantes.length) return `Pièce(s) obligatoire(s) à déposer : ${manquantes.map((x) => x.libelle).join(', ')}.`;
    return pieces.some((x) => x.courante) ? null : 'Aucune nouvelle pièce à envoyer.';
};

function StageDepotPublic({ token, entete, apercu = null }) {
    const [identite, setIdentite] = useState({ numero: '', dateNaissance: '' });
    const [etat, setEtat] = useState(apercu);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [erreurPiece, setErreurPiece] = useState({});
    const [envoye, setEnvoye] = useState(null);
    const d = etat?.dossier || entete?.dossier || {};

    const identifier = useCallback(async (e) => {
        e.preventDefault();
        setEnCours(true); setErreur(null);
        try {
            const r = await stagePublicAPI.identifierDepot(token, identite);
            setEtat(r.data);
        } catch (err) {
            setErreur(err.response?.data?.error || 'Identification impossible. Réessayez, ou contactez le secrétariat des stages.');
        } finally { setEnCours(false); }
    }, [token, identite]);

    const deposer = useCallback(async (piece, fichier, refus) => {
        setErreurPiece((x) => ({ ...x, [piece.attendu_id]: null }));
        if (refus) { setErreurPiece((x) => ({ ...x, [piece.attendu_id]: refus })); return; }
        // ⚠️ Contrôlé ici pour ne pas faire attendre un transfert voué à l'échec —
        // et REcontrôlé par le serveur, qui ne croit pas l'écran.
        if (fichier.size > etat.taille_max_octets) {
            setErreurPiece((x) => ({ ...x, [piece.attendu_id]:
                `Votre fichier fait ${tailleMo(fichier.size)}, la limite est ${tailleMo(etat.taille_max_octets)} — scannez-le en noir et blanc ou réduisez sa résolution.` }));
            return;
        }
        if (apercu) {
            setEtat((e) => {
                const pieces = e.pieces.map((x) => (x.attendu_id !== piece.attendu_id ? x : { ...x, courante: {
                    statut: 'depose', soumise: false, nom_fichier: fichier.name, taille_octets: fichier.size, depose_le: new Date().toISOString() } }));
                return { ...e, pieces, raison_non_soumissible: raisonApercu(pieces) };
            });
            return;
        }
        setEnCours(true);
        try {
            const r = await stagePublicAPI.deposerPiece(token, piece.attendu_id, identite, fichier);
            setEtat(r.data);
        } catch (err) {
            setErreurPiece((x) => ({ ...x, [piece.attendu_id]: err.response?.data?.error || "Le dépôt n'a pas abouti. Réessayez." }));
        } finally { setEnCours(false); }
    }, [apercu, token, identite, etat]);

    const telechargerConvention = useCallback(async () => {
        setErreur(null);
        if (apercu) { setErreur('Aperçu : le téléchargement de la convention est désactivé.'); return; }
        try {
            const r = await stagePublicAPI.telechargerConvention(token, identite);
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = `Convention de stage — ${d.nom || ''} ${d.prenom || ''}.pdf`.trim();
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
            setErreur('La convention n\'a pas pu être téléchargée. Réessayez, ou contactez le secrétariat.');
        }
    }, [apercu, token, identite, d.nom, d.prenom]);

    const envoyer = useCallback(async () => {
        if (apercu) { setEnvoye('Aperçu : rien n’a été envoyé. Dans la vraie page, l’étudiant verrait ici la confirmation de son envoi.'); return; }
        setEnCours(true); setErreur(null);
        try {
            const r = await stagePublicAPI.envoyerDepot(token, identite);
            setEtat(r.data);
            setEnvoye(r.data.message);
        } catch (err) {
            setErreur(err.response?.data?.error || "L'envoi n'a pas abouti. Réessayez.");
        } finally { setEnCours(false); }
    }, [apercu, token, identite]);

    const enTete = (
        <div className="form-public-header">
            <h2><i className="fa-solid fa-folder-open" /> Dépôt de documents — Stage</h2>
            <p>{d.departement_nom}{d.formation_nom ? ` · ${d.formation_nom}` : ''}{d.semestre ? ` · ${d.semestre}` : ''}</p>
            <p><b>{d.nom} {d.prenom}</b>{d.date_debut ? ` — stage du ${dateFr(d.date_debut)} au ${dateFr(d.date_fin)}` : ''}</p>
        </div>
    );

    if (envoye) {
        return (
            <Message ton="succes" icone="fa-circle-check" titre="Documents envoyés">
                <p>{envoye}</p>
                <p className="pub-discret">Vous recevrez un courriel si une pièce doit être corrigée. Vous pouvez fermer cette page.</p>
            </Message>
        );
    }

    // ── L'identification (E82) ──────────────────────────────────────────────
    if (!etat) {
        return (
            <div className="pub-page">
                <form className="form-public" onSubmit={identifier}>
                    {enTete}
                    <div className="form-public-body">
                        <div className="form-section">
                            <h3><i className="fa-solid fa-id-card" /> Identifiez-vous</h3>
                            <p className="pub-aide pub-aide-bloc">
                                Pour accéder à votre convention et déposer vos documents, confirmez votre identité.
                                Si vous avez reçu ce lien par erreur, ne l'utilisez pas.
                            </p>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="dep-num">Votre numéro étudiant <span className="form-required">*</span></label>
                                    <input id="dep-num" value={identite.numero} autoFocus autoComplete="off" placeholder="Ex : 20257566"
                                           onChange={(e) => setIdentite({ ...identite, numero: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dep-naiss">Votre date de naissance <span className="form-required">*</span></label>
                                    <ChampDate id="dep-naiss" value={identite.dateNaissance}
                                           onChange={(e) => setIdentite({ ...identite, dateNaissance: e.target.value })} />
                                </div>
                            </div>
                            {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                        </div>
                    </div>
                    <div className="form-public-footer">
                        <button type="submit" className="pub-btn" disabled={enCours || !identite.numero || !identite.dateNaissance}>
                            {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Vérification…</>
                                     : <><i className="fa-solid fa-arrow-right" /> Accéder à mon dépôt</>}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // ── Le dépôt ────────────────────────────────────────────────────────────
    return (
        <div className="pub-page">
            <div className="form-public">
                {enTete}
                <div className="form-public-body">
                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit l'étudiant ; rien n'est envoyé.
                        </div>
                    )}
                    {etat.convention_disponible && (
                        <div className="form-section form-section-accent">
                            <h3><i className="fa-solid fa-file-signature" /> Votre convention</h3>
                            <p className="pub-aide pub-aide-bloc">
                                Téléchargez-la, faites-la signer par toutes les parties, puis déposez-la ci-dessous.
                            </p>
                            <button type="button" className="pub-btn pub-btn-secondaire" onClick={telechargerConvention}>
                                <i className="fa-solid fa-download" /> Télécharger ma convention
                            </button>
                        </div>
                    )}

                    {etat.message && <div className="pub-encart pub-encart-info"><i className="fa-solid fa-circle-info" /> {etat.message}</div>}

                    <div className="form-section">
                        <h3><i className="fa-solid fa-file-arrow-up" /> Documents à déposer</h3>
                        <p className="pub-aide pub-aide-bloc">
                            Chaque document est enregistré dès que vous le choisissez. Les documents obligatoires (<span className="form-required">*</span>)
                            sont requis pour envoyer votre dossier.
                        </p>
                        {etat.pieces.map((p) => (
                            <div key={p.attendu_id}>
                                <Piece piece={p} ouvert={etat.ouvert} tailleMax={etat.taille_max_octets}
                                       enCours={enCours} onFichier={deposer} />
                                {erreurPiece[p.attendu_id] && (
                                    <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreurPiece[p.attendu_id]}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                </div>
                {etat.ouvert && (
                    <div className="form-public-footer pub-pied-depot">
                        {/* ⚠️ Grisé ET il dit pourquoi, en toutes lettres (E69) — sur SA ligne :
                            à côté du bouton, le texte débordait dessous (vu à l'image). */}
                        {etat.raison_non_soumissible && <div className="pub-aide pub-raison">{etat.raison_non_soumissible}</div>}
                        <button type="button" className="pub-btn" onClick={envoyer}
                                disabled={enCours || !!etat.raison_non_soumissible}>
                            {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Envoi…</>
                                     : <><i className="fa-solid fa-paper-plane" /> Envoyer mes documents</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StageDepotPublic;
